import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tag, Tooltip, Progress } from "antd";
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

const DEFAULT_EXPIRATION_CONFIG = {
  pendingExpiryDays: 30,
  sentExpiryDays: 30,
  negotiatingExpiryDays: 45,
};

const ExpirationCountdown = ({ quote, expirationConfig = {} }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [percentage, setPercentage] = useState(0);

  const config = useMemo(
    () => ({ ...DEFAULT_EXPIRATION_CONFIG, ...expirationConfig }),
    [expirationConfig]
  );

  // Memoize the quote ID and status to prevent unnecessary re-renders
  const quoteKey = useMemo(
    () => `${quote?.id}-${quote?.status}`,
    [quote?.id, quote?.status]
  );

  // Memoize the calculateTimeLeft function to prevent recreation on every render
  const calculateTimeLeft = useCallback(() => {
    if (!quote) return;

    const now = new Date();
    const status = quote.status;

    let expiryDays;
    switch (status) {
      case "pending":
        expiryDays = config.pendingExpiryDays;
        break;
      case "sent":
        expiryDays = config.sentExpiryDays;
        break;
      case "negotiating":
        expiryDays = config.negotiatingExpiryDays;
        break;
      default:
        return; // Other statuses don't expire
    }

    const validityStart = new Date(
      quote.validFrom ||
        quote.revisedAt ||
        (status === "pending"
          ? quote.createdAt
          : quote.sentAt || quote.createdAt)
    );
    const explicitExpiry =
      quote.validUntil != null ? new Date(quote.validUntil) : null;
    const expiryDate =
      explicitExpiry && Number.isFinite(explicitExpiry.getTime())
        ? explicitExpiry
        : new Date(
            validityStart.getTime() + expiryDays * 24 * 60 * 60 * 1000
          );
    if (!Number.isFinite(expiryDate.getTime())) return;
    const timeDiff = expiryDate.getTime() - now.getTime();

    if (timeDiff <= 0) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, expired: true });
      setPercentage(100);
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    setTimeLeft({ days, hours, minutes, expired: false });

    // Calculate percentage for progress bar
    const totalTime = Number.isFinite(validityStart.getTime())
      ? expiryDate.getTime() - validityStart.getTime()
      : expiryDays * 24 * 60 * 60 * 1000;
    const elapsedTime = totalTime - timeDiff;
    const progressPercentage = Math.min((elapsedTime / totalTime) * 100, 100);
    setPercentage(progressPercentage);
  }, [
    quote,
    config.pendingExpiryDays,
    config.sentExpiryDays,
    config.negotiatingExpiryDays,
  ]);

  useEffect(() => {
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [quoteKey, calculateTimeLeft]);

  if (
    !timeLeft ||
    quote.status === "expired" ||
    quote.status === "accepted" ||
    quote.status === "ordered"
  ) {
    return null;
  }

  const getExpirationColor = () => {
    if (timeLeft.expired) return "red";
    if (timeLeft.days <= 1) return "red";
    if (timeLeft.days <= 3) return "orange";
    if (timeLeft.days <= 7) return "blue";
    return "green";
  };

  const getExpirationIcon = () => {
    if (timeLeft.expired) return <ExclamationCircleOutlined />;
    if (timeLeft.days <= 1) return <ExclamationCircleOutlined />;
    if (timeLeft.days <= 3) return <ClockCircleOutlined />;
    return <ClockCircleOutlined />;
  };

  const formatTimeLeft = () => {
    if (timeLeft.expired) return "EXPIRED";
    if (timeLeft.days > 0) return `${timeLeft.days}d ${timeLeft.hours}h`;
    if (timeLeft.hours > 0) return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    return `${timeLeft.minutes}m`;
  };

  const getExpirationMessage = () => {
    if (timeLeft.expired) return "This quote has expired";
    if (timeLeft.days <= 1) return "This quote expires very soon!";
    if (timeLeft.days <= 3) return "This quote expires soon";
    if (timeLeft.days <= 7) return "This quote is approaching expiration";
    return "This quote has plenty of time before expiration";
  };

  const getProgressColor = () => {
    if (percentage >= 90) return "#cf1322"; // Red
    if (percentage >= 75) return "#fa8c16"; // Orange
    if (percentage >= 50) return "#1890ff"; // Blue
    return "#52c41a"; // Green
  };

  return (
    <div style={{ marginBottom: "8px" }}>
      <Tooltip title={getExpirationMessage()}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tag
            color={getExpirationColor()}
            icon={getExpirationIcon()}
            style={{ margin: 0 }}
          >
            {formatTimeLeft()}
          </Tag>

          {/* Progress bar for visual representation */}
          <div style={{ flex: 1, maxWidth: "100px" }}>
            <Progress
              percent={percentage}
              size="small"
              showInfo={false}
              strokeColor={getProgressColor()}
              trailColor="#f0f0f0"
            />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default ExpirationCountdown;
